// frontend/src/pages/admin/EmployeesManagement.js

import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '../../components/AuthContext';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/20/solid';
import Swal from 'sweetalert2';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { FaMoon, FaSun } from 'react-icons/fa';

const EmployeeManagement = () => {
  const { user } = useContext(AuthContext);

  // Dark mode
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('emp-dark-mode') === 'true');
  const toggleDarkMode = () => setDarkMode(prev => { localStorage.setItem('emp-dark-mode', !prev); return !prev; });

  // Dark mode utility classes
  const bg = darkMode ? 'bg-gray-900' : 'bg-white';
  const text = darkMode ? 'text-gray-100' : 'text-gray-900';
  const textMuted = darkMode ? 'text-gray-400' : 'text-gray-500';
  const textLabel = darkMode ? 'text-gray-300' : 'text-gray-700';
  const border = darkMode ? 'border-gray-700' : 'border-gray-200';
  const inputCls = darkMode
    ? 'bg-gray-800 border-gray-600 text-gray-100 placeholder-gray-500 focus:ring-blue-400'
    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:ring-blue-500';
  const cardBg = darkMode ? 'bg-gray-800' : 'bg-gray-50';
  const modalBg = darkMode ? 'bg-gray-800' : 'bg-white';
  const headerBg = darkMode ? 'bg-gray-700' : 'bg-gray-50';
  const hoverRow = darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50';

  // react-select dark mode styles
  const selectStyles = darkMode ? {
    control: (base) => ({ ...base, backgroundColor: '#1f2937', borderColor: '#4b5563', minHeight: 42, boxShadow: 'none', '&:hover': { borderColor: '#6b7280' } }),
    menu: (base) => ({ ...base, backgroundColor: '#1f2937', border: '1px solid #4b5563' }),
    option: (base, state) => ({ ...base, backgroundColor: state.isSelected ? '#3b82f6' : state.isFocused ? '#374151' : '#1f2937', color: '#f3f4f6' }),
    singleValue: (base) => ({ ...base, color: '#f3f4f6' }),
    placeholder: (base) => ({ ...base, color: '#9ca3af' }),
    input: (base) => ({ ...base, color: '#f3f4f6' }),
    indicatorSeparator: (base) => ({ ...base, backgroundColor: '#4b5563' }),
    dropdownIndicator: (base) => ({ ...base, color: '#9ca3af' }),
  } : {};

  // -------------------------------------------------------------------------
  // States
  // -------------------------------------------------------------------------
  const [roles, setRoles] = useState([]);
  const [locations, setLocations] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [totalEmployees, setTotalEmployees] = useState(0);
  const [filter, setFilter] = useState('');
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(5);

  // Editing states
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);

  // For previewing uploaded images
  const [imagePreview, setImagePreview] = useState(null);

  // Modal states for viewing details
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);

  const currentPage = page + 1;
  // Initial form object
  const initialForm = {
    company: user?.user?.company?._id || user?.user?.company,
    companyHead: user?.user?._id,
    email: '',
    name: '',
    gender: '',
    dateOfBirth: null,
    dateOfJoining: null,
    aadharNo: '',
    panNo: '',
    image: null,
    phoneNumbers: [{ number: '', countryCode: '+91' }],
    postalAddress: '',
    fatherName: '',
    motherName: '',
    spouseName: '',
    children: [{ name: '', dateOfBirth: null, gender: '' }],
    accountName: '',
    accountNo: '',
    bankName: '',
    ifsc: '',
    pfNo: '',
    esicNo: '',
    role: '',
    password: '',
    assignedLocation: '',
    attendanceType: 'Manual', // default to Manual
    isSalesman: false, // default to false
    isTT: false, // default to false
    draftSettings: {
      draftEnabled: true,
      draftDuration: '1week'
    },
    createdBy: user?.user?._id,
    updatedBy: user?.user?._id,
    isActive: true,
  };

  const [form, setForm] = useState(initialForm);

  // Options for react-select components
  const genderOptions = [
    { value: 'Male', label: 'Male' },
    { value: 'Female', label: 'Female' },
    { value: 'Other', label: 'Other' }
  ];

  const attendanceTypeOptions = [
    { value: 'App', label: 'App' },
    { value: 'Manual', label: 'Manual' }
  ];

  const draftDurationOptions = [
    { value: '1day', label: '1 Day' },
    { value: '3days', label: '3 Days' },
    { value: '1week', label: '1 Week' },
    { value: '2weeks', label: '2 Weeks' },
    { value: '1month', label: '1 Month' },
    { value: '3months', label: '3 Months' }
  ];

  const limitOptions = [
    { value: 5, label: '5' },
    { value: 10, label: '10' },
    { value: 20, label: '20' }
  ];

  // -------------------------------------------------------------------------
  // Lifecycle
  // -------------------------------------------------------------------------
  useEffect(() => {
    fetchLocations();
    fetchEmployees();
    fetchRoles();
    // eslint-disable-next-line
  }, [page, limit]);

  useEffect(() => {
    // Re-fetch employees if filter changes
    fetchEmployees();    
   // eslint-disable-next-line
  }, [filter, page, limit]);

  // -------------------------------------------------------------------------
  // Data fetching
  // -------------------------------------------------------------------------
  const fetchLocations = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/locations/companyId/${user.user.company._id || user.user.company}`,
      );
      setLocations(response.data);
    } catch (error) {
      Swal.fire('Error', 'Failed to fetch locations', 'error');
    }
  };

  const fetchEmployees = async () => {
    try {
      const countRes = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/employees/count`,
        { params: { companyId: (user.user.company._id || user.user.company), filter } }
      );
      setTotalEmployees(countRes.data.count);

      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/employees/company/${user.user.company._id || user.user.company}`,
        { params: { page, limit, filter } }
      );
      setFilteredEmployees(response.data.employees);
    } catch (error) {
      Swal.fire('Error', 'Failed to fetch employees', 'error');
    }
  };

  const fetchRoles = async () => {
    try {
      const token = localStorage.getItem('token');
      const api = axios.create({
        baseURL: process.env.REACT_APP_API_BASE_URL,
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      // Make sure we have a valid company ID
      const companyId = user?.user?.company?._id || user?.user?.company;
      
      if (!companyId) {
        console.error('Company ID not available');
        Swal.fire("Error", "Company ID not available", "error");
        return;
      }
      
      console.log('Fetching roles for company:', companyId);
      
      // Fetch roles for this company with error logging
      try {
        const response = await api.get(`/roles/company/${companyId}`);
        console.log('Roles received:', response.data);
        setRoles(response.data);
      } catch (err) {
        console.error('API error fetching roles:', err.response || err);
        throw err;
      }
    } catch (error) {
      console.error('Error fetching roles:', error);
      Swal.fire("Error", "Failed to load roles", "error");
    }
  };

  // -------------------------------------------------------------------------
  // Form Handlers
  // -------------------------------------------------------------------------
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // List of fields that should be converted to uppercase
    const uppercaseFields = [
      'name', 'fatherName', 'motherName', 'spouseName', 'postalAddress',
      'accountName', 'bankName', 'aadharNo', 'panNo', 'pfNo', 'esicNo', 'ifsc'
    ];
    
    // Convert to uppercase if field is in the list, handle undefined/null values
    const processedValue = uppercaseFields.includes(name) && value ? value.toUpperCase() : (value || '');
    
    setForm((prev) => ({ ...prev, [name]: processedValue }));
  };

  // Handler for react-select components
  const handleSelectChange = (selectedOption, actionMeta) => {
    const fieldName = actionMeta.name;
    const value = selectedOption ? selectedOption.value : '';
    
    setForm((prev) => ({ ...prev, [fieldName]: value }));
  };

  // Handler for child gender select
  const handleChildSelectChange = (index, selectedOption) => {
    const value = selectedOption ? selectedOption.value : '';
    handleChildChange(index, 'gender', value);
  };

  // Handler for limit select
  const handleLimitChange = (selectedOption) => {
    const value = selectedOption ? selectedOption.value : 5;
    setLimit(value);
  };

  // Handler for DatePicker components
  const handleDateChange = (date, fieldName) => {
    setForm((prev) => ({ ...prev, [fieldName]: date }));
  };

  // Handler for child date picker
  const handleChildDateChange = (index, date) => {
    handleChildChange(index, 'dateOfBirth', date);
  };

  // Calculate age from date
  const calculateAge = (birthDate, referenceDate = null) => {
    if (!birthDate) return '';
    const reference = referenceDate || new Date();
    const birth = new Date(birthDate);
    let age = reference.getFullYear() - birth.getFullYear();
    const monthDiff = reference.getMonth() - birth.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && reference.getDate() < birth.getDate())) {
      age--;
    }
    
    return age;
  };

  // Get minimum date for employee (18 years ago)
  const getMinEmployeeDate = () => {
    const date = new Date();
    date.setFullYear(date.getFullYear() - 18);
    return date;
  };

  // Get maximum date for employee (65 years ago for reasonable range)
  const getMaxEmployeeDate = () => {
    const date = new Date();
    date.setFullYear(date.getFullYear() - 65);
    return date;
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/gif'];
    
    if (file && !validTypes.includes(file.type)) {
      Swal.fire('Error', 'Please select a valid image (PNG, JPG, GIF)', 'error');
      e.target.value = '';
      setForm((prev) => ({ ...prev, image: null }));
      setImagePreview(null);
      return;
    }
    if (file) {
      setForm((prev) => ({ ...prev, image: file }));
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const addPhoneNumber = () => {
    setForm((prev) => ({
      ...prev,
      phoneNumbers: [...prev.phoneNumbers, { number: '', countryCode: '+91' }],
    }));
  };

  const removePhoneNumber = (idx) => {
    setForm((prev) => ({
      ...prev,
      phoneNumbers: prev.phoneNumbers.filter((_, i) => i !== idx),
    }));
  };

  const handlePhoneNumberChange = (idx, value) => {
    setForm((prev) => {
      const updated = [...prev.phoneNumbers];
      updated[idx].number = value;
      return { ...prev, phoneNumbers: updated };
    });
  };

  const handleCountryCodeChange = (idx, value) => {
    setForm((prev) => {
      const updated = [...prev.phoneNumbers];
      updated[idx].countryCode = value;
      return { ...prev, phoneNumbers: updated };
    });
  };

  const addChild = () => {
    setForm((prev) => ({
      ...prev,
      children: [...prev.children, { name: '', dateOfBirth: null, gender: '' }],
    }));
  };

  const removeChild = (idx) => {
    setForm((prev) => ({
      ...prev,
      children: prev.children.filter((_, i) => i !== idx),
    }));
  };

  const handleChildChange = (idx, field, value) => {
    setForm((prev) => {
      const updatedChildren = [...prev.children];
      // Convert child name to uppercase
      const processedValue = field === 'name' ? value.toUpperCase() : value;
      updatedChildren[idx] = { ...updatedChildren[idx], [field]: processedValue };
      return { ...prev, children: updatedChildren };
    });
  };

  const resetForm = () => {
    setForm(initialForm);
    setImagePreview(null);
    setIsEditing(false);
    setEditingId(null);
  };

  // -------------------------------------------------------------------------
  // Validate
  // -------------------------------------------------------------------------
  const validateAndAlert = (employee) => {
    const requiredFields = [
      'name',
      'email',
      'gender',
      'dateOfBirth',
      'dateOfJoining',
      'aadharNo',
      'panNo',
      'phoneNumbers',
      'postalAddress',
      'fatherName',
      'motherName',
      'accountName',
      'accountNo',
      'bankName',
      'ifsc',
      'pfNo',
      'esicNo',
      'role',
      'password',
    ];

    const missing = [];
    requiredFields.forEach((field) => {
      const val = employee[field];
      if (
        val === undefined ||
        val === null ||
        val === '' ||
        (Array.isArray(val) && val.length === 0)
      ) {
        missing.push(field);
      }
    });
    if (missing.length > 0) {
      Swal.fire({
        icon: 'error',
        title: 'Validation Failed',
        html: 'Please fill in all required fields.',
      });
      return false;
    }
    return true;
  };

  const validateForm = () => {
    const { panNo, aadharNo, phoneNumbers, email } = form;
    // More comprehensive email validation (RFC 5322 compliant)
    const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
    const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
    const aadharRegex = /^[0-9]{12}$/;

    // Email validation
    if (!email || !email.trim()) {
      Swal.fire('Error', 'Email is required', 'error');
      return false;
    }
    
    if (!emailRegex.test(email.trim())) {
      Swal.fire('Error', 'Invalid email format. Please enter a valid email address.', 'error');
      return false;
    }
    
    if (email.trim().length > 254) {
      Swal.fire('Error', 'Email address is too long (max 254 characters)', 'error');
      return false;
    }
    
    // Check for disposable email domains
    const disposableDomains = ['tempmail.com', 'throwaway.email', '10minutemail.com', 'guerrillamail.com'];
    const emailDomain = email?.trim()?.toLowerCase()?.split('@')[1];
    if (emailDomain && disposableDomains.includes(emailDomain)) {
      Swal.fire('Error', 'Disposable email addresses are not allowed', 'error');
      return false;
    }
    
    // Check for spaces in email
    if (email.includes(' ')) {
      Swal.fire('Error', 'Email cannot contain spaces', 'error');
      return false;
    }
    
    if (!panRegex.test(panNo)) {
      Swal.fire('Error', 'Invalid PAN Number', 'error');
      return false;
    }
    if (!aadharRegex.test(aadharNo)) {
      Swal.fire('Error', 'Invalid Aadhar Number', 'error');
      return false;
    }
    for (const phone of phoneNumbers) {
      if (!phone.number) {
        Swal.fire('Error', 'Phone number is required', 'error');
        return false;
      }
      // Only digits allowed
      if (!/^\d+$/.test(phone.number)) {
        Swal.fire('Error', 'Phone number must contain only digits', 'error');
        return false;
      }
      // For India (+91), enforce 10 digits; others allow 7-15 digits
      if ((phone.countryCode || '+91') === '+91') {
        if (phone.number.length !== 10) {
          Swal.fire('Error', 'Indian phone number must be 10 digits', 'error');
          return false;
        }
      } else {
        if (phone.number.length < 7 || phone.number.length > 15) {
          Swal.fire('Error', 'Phone number must be between 7 and 15 digits', 'error');
          return false;
        }
      }
    }
    return true;
  };

  const checkExistingAccount = async () => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/auth/check-account`,
        { email: form.email }
      );
      return response.data.exists;
    } catch (error) {
      Swal.fire('Error', 'Failed to check for existing account', 'error');
      return false;
    }
  };

  // -------------------------------------------------------------------------
  // Submit
  // -------------------------------------------------------------------------
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateAndAlert(form)) return;

    const exists = await checkExistingAccount();
    if (!isEditing && exists) {
      Swal.fire(
        'Warning',
        'Employee must be associated with an existing account with any role',
        'warning'
      );
      return;
    }
    if (!validateForm()) return;

    // Check if email has changed during editing
    if (isEditing && selectedEmployee && form.email && selectedEmployee.email !== form.email.trim().toLowerCase()) {
      const newEmail = form.email?.trim()?.toLowerCase() || form.email;
      const confirmResult = await Swal.fire({
        title: 'Email Changed',
        html: `
          <p>You are changing the email address from:</p>
          <p><strong>${selectedEmployee.email}</strong></p>
          <p>to:</p>
          <p><strong>${newEmail}</strong></p>
          <br>
          <p class="text-warning">⚠️ This will reset the employee's email verification status and login count.</p>
          <p>The employee will need to verify their new email address.</p>
        `,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, update email',
        cancelButtonText: 'Cancel',
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33'
      });
      
      if (!confirmResult.isConfirmed) {
        return;
      }
    }

    // Build form data
    const formData = new FormData();
    Object.keys(form).forEach((key) => {
      if (key === 'phoneNumbers') {
        formData.append(key, JSON.stringify(form[key]));
      } else if (key === 'children') {
        // Format children data with proper date formatting
        const formattedChildren = form[key].map(child => ({
          ...child,
          dateOfBirth: child.dateOfBirth ? child.dateOfBirth.toISOString().split('T')[0] : ''
        }));
        formData.append(key, JSON.stringify(formattedChildren));
      } else if (key === 'draftSettings') {
        formData.append(key, JSON.stringify(form[key]));
      } else if (key === 'image') {
        if (form.image) formData.append('image', form.image);
      } else if (key === 'dateOfBirth' || key === 'dateOfJoining') {
        // Format dates properly for API
        const dateValue = form[key] ? form[key].toISOString().split('T')[0] : '';
        formData.append(key, dateValue);
      } else {
        formData.append(key, form[key]);
      }
    });

    try {
      let data;
      if (isEditing) {
        data = await axios.put(
          `${process.env.REACT_APP_API_BASE_URL}/employees/${editingId}`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
        setTimeout(() => {
          if (data.status === 201) {
            Swal.fire('Success', 'Employee updated successfully', 'success');
          } else {
            Swal.fire('Error', 'Failed to update employee', 'error');
          }
        }, 1000);
      } else {
        data = await axios.post(
          `${process.env.REACT_APP_API_BASE_URL}/employees`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
        setTimeout(() => {
          if (data.status === 201) {
            Swal.fire('Success', 'Employee added successfully', 'success');
          } else {
            Swal.fire('Error', 'Failed to add employee', 'error');
          }
        }, 1000);
      }
      resetForm();
      fetchEmployees();
    } catch (err) {
      Swal.fire('Error', 'Employee submission failed', 'error');
    }
  };

  const toggleActive = async (employee) => {
    Swal.fire({
      title: employee.isActive ? "Deactivate Account" : "Activate Account",
      text: `Are you sure you want to ${employee.isActive ? "deactivate" : "activate"} this account?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, proceed!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await axios.patch(
            `${process.env.REACT_APP_API_BASE_URL}/employees/toggle-active/${employee._id}`,
            { isActive: !employee.isActive }
          );
          Swal.fire("Success", "Employee status updated", "success");
          fetchEmployees();
        } catch (error) {
          Swal.fire("Error", "Failed to update employee status" + error.message, "error");
        }
      }
    });
  };

  // -------------------------------------------------------------------------
  // Editing
  // -------------------------------------------------------------------------
  const startEditing = (employee) => {
    setIsEditing(true);
    setEditingId(employee._id);
    setSelectedEmployee(employee); // Store original employee data for comparison
    setForm({
      ...employee,
      password: '',
      dateOfBirth: employee.dateOfBirth
        ? new Date(employee.dateOfBirth)
        : null,
      dateOfJoining: employee.dateOfJoining
        ? new Date(employee.dateOfJoining)
        : null,
      phoneNumbers: employee.phoneNumbers.map((ph) => ({ number: ph.number, countryCode: ph.countryCode || '+91' })),
      children: employee.children ? employee.children.map(child => ({
        ...child,
        dateOfBirth: child.dateOfBirth ? new Date(child.dateOfBirth) : null
      })) : [{ name: '', dateOfBirth: null, gender: '' }],
      attendanceType: employee.attendanceType || 'Manual',
      isSalesman: employee.isSalesman || false,
      isTT: employee.isTT || false,
      draftSettings: employee.draftSettings || {
        draftEnabled: true,
        draftDuration: '1week'
      },
    });
    setImagePreview(
      employee.image
        ? `${process.env.REACT_APP_API_BASE_URL}/employees/image/${employee._id}`
        : null
    );
  };

  // -------------------------------------------------------------------------
  // View Details (opens modal)
  // -------------------------------------------------------------------------
  const handleViewDetails = (employee) => {
    setSelectedEmployee(employee);
    setShowDetailsModal(true);
  };

  // -------------------------------------------------------------------------
  // Pagination
  // -------------------------------------------------------------------------
  const handlePreviousPage = () => {
    if (currentPage > 1) setPage((prev) => prev - 1);
  };

  const handleNextPage = () => {
    if (currentPage < Math.ceil(totalEmployees / limit)) {
      setPage((prev) => prev + 1);
    }
  };

  // -------------------------------------------------------------------------
  // Modal for showing Employee Details
  // -------------------------------------------------------------------------
  const DetailsModal = () => {
    if (!selectedEmployee) return null;

    // For convenience, destructure the selectedEmployee fields
    const {
      _id,
      name,
      gender,
      dateOfBirth,
      dateOfJoining,
      aadharNo,
      panNo,
      attendanceType,
      phoneNumbers,
      postalAddress,
      fatherName,
      motherName,
      spouseName,
      children,
      accountName,
      accountNo,
      bankName,
      ifsc,
      pfNo,
      esicNo,
    } = selectedEmployee;

    // Convert dates to more readable strings
    const dob = dateOfBirth ? new Date(dateOfBirth).toLocaleDateString() : '';
    const doj = dateOfJoining ? new Date(dateOfJoining).toLocaleDateString() : '';

    // Retrieve the image if available
    const imageUrl = `${process.env.REACT_APP_API_BASE_URL}/employees/image/${_id}`;

    return (
      <div className="fixed inset-0 z-50 overflow-y-auto">
        {/* Overlay */}
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          aria-hidden="true"
        />
        <div className="flex items-center justify-center min-h-screen">
          <div className={`relative ${modalBg} rounded-lg overflow-hidden shadow-xl transform transition-all max-w-4xl w-full m-4`}>
            {/* Modal Header */}
            <div className={`flex justify-between items-center px-4 py-2 ${headerBg} border-b ${border}`}>
              <h3 className={`text-lg font-semibold ${text}`}>Employee Details</h3>
              <button
                className={`${textMuted} hover:text-red-500`}
                onClick={() => setShowDetailsModal(false)}
              >
                ✕
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-4 md:p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Image */}
              <div className="md:col-span-1 flex justify-center">
                {selectedEmployee.image ? (
                  <img
                    src={imageUrl}
                    alt="Employee"
                    className="max-w-full h-auto rounded"
                  />
                ) : (
                  <div className={`w-full h-32 flex items-center justify-center ${darkMode ? 'bg-gray-700' : 'bg-gray-200'} rounded`}>
                    <span className={textMuted}>No Image</span>
                  </div>
                )}
              </div>
              {/* Details */}
              <div className="md:col-span-2 space-y-4">
                <div>
                  <p>
                    <strong>Name:</strong> {name}
                  </p>
                  <p>
                    <strong>Gender:</strong> {gender}
                  </p>
                  <p>
                    <strong>Date of Birth:</strong> {dob}
                  </p>
                  <p>
                    <strong>Date of Joining:</strong> {doj}
                  </p>
                  <p>
                    <strong>Aadhar Number:</strong> {aadharNo}
                  </p>
                  <p>
                    <strong>PAN Number:</strong> {panNo}
                  </p>                  <p>
                    <strong>Attendance Type:</strong> {attendanceType || 'N/A'}
                  </p>
                  <p>
                    <strong>Is Salesperson:</strong> {selectedEmployee.isSalesman ? 'Yes' : 'No'}
                  </p>
                  <p className={textMuted}>
                    <strong>TT Assignment Access:</strong> {selectedEmployee.isTT ? 'Yes' : 'No'}
                  </p>
                  <p className={textMuted}>
                    <strong>Is Verified:</strong> {selectedEmployee.isVerified ? 'Yes' : 'No'}
                  </p>
                  <p>
                    <strong>Phone Numbers:</strong>{' '}
                    {phoneNumbers.map((p) => `${p.countryCode || '+91'} ${p.number}`).join(', ')}
                  </p>
                  <p>
                    <strong>Postal Address:</strong> {postalAddress}
                  </p>
                </div>
                <div>
                  <p>
                    <strong>Father's Name:</strong> {fatherName}
                  </p>
                  <p>
                    <strong>Mother's Name:</strong> {motherName}
                  </p>
                  <p>
                    <strong>Spouse's Name:</strong> {spouseName}
                  </p>
                  <p>
                    <strong>Children:</strong>{' '}
                    {children.map((c) => c.name).join(', ')}
                  </p>
                </div>
                <div>
                  <p>
                    <strong>Account Name:</strong> {accountName}
                  </p>
                  <p>
                    <strong>Account Number:</strong> {accountNo}
                  </p>
                  <p>
                    <strong>Bank Name:</strong> {bankName}
                  </p>
                  <p>
                    <strong>IFSC Code:</strong> {ifsc}
                  </p>
                  <p>
                    <strong>PF Number:</strong> {pfNo}
                  </p>
                  <p>
                    <strong>ESIC Number:</strong> {esicNo}
                  </p>
                </div>
              </div>

              {/* Draft Settings Section */}
              <div className={`${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-blue-50 border-blue-200'} p-4 rounded-lg border mt-4`}>
                <h4 className={`font-semibold ${darkMode ? 'text-blue-300' : 'text-blue-800'} mb-3`}>Draft Settings</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <p>
                    <strong>Draft Feature:</strong>{' '}
                    <span className={selectedEmployee.draftSettings?.draftEnabled ? 'text-green-600 font-semibold' : 'text-red-600 font-semibold'}>
                      {selectedEmployee.draftSettings?.draftEnabled ? 'Enabled' : 'Disabled'}
                    </span>
                  </p>
                  <p>
                    <strong>Retention Duration:</strong>{' '}
                    {(() => {
                      const duration = selectedEmployee.draftSettings?.draftDuration || '1week';
                      const durationMap = {
                        '1day': '1 Day',
                        '3days': '3 Days',
                        '1week': '1 Week',
                        '2weeks': '2 Weeks',
                        '1month': '1 Month',
                        '3months': '3 Months'
                      };
                      return durationMap[duration] || '1 Week';
                    })()}
                  </p>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className={`px-4 py-3 ${headerBg} border-t ${border} flex justify-end`}>
              <button
                className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                onClick={() => setShowDetailsModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Export employees to Excel
  const handleExportExcel = async () => {
    try {
      const companyId = user?.user?.company?._id || user?.user?.company;
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/employees/export/excel/${companyId}`,
        {
          responseType: 'blob',
        }
      );

      // Create blob link to download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `employees_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      Swal.fire('Success', 'Employees exported successfully!', 'success');
    } catch (error) {
      console.error('Error exporting employees:', error);
      Swal.fire('Error', 'Failed to export employees', 'error');
    }
  };

  return (
    <div className={`w-full mx-auto ${bg} shadow rounded p-6 my-6 ${text}`}>
      {/* Dark Mode Toggle + Heading */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl md:text-3xl font-bold">
          {isEditing ? 'Edit Employee' : 'Add New Employee'}
        </h2>
        <button onClick={toggleDarkMode} className={`p-2 rounded-lg ${darkMode ? 'bg-gray-700 text-yellow-400 hover:bg-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} title="Toggle dark mode">
          {darkMode ? <FaSun /> : <FaMoon />}
        </button>
      </div>

      {/* FORM */}
      <form onSubmit={handleSubmit} autoComplete="off" className="space-y-6">
        {/* Basic Info Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Name */}
          <div>
            <label className={`block ${textLabel} font-semibold mb-1`}>
              Full Name
            </label>
            <input
              type="text"
              name="name"
              value={form.name}
              onChange={handleInputChange}
              placeholder="Full Name"
              className={`w-full p-2 border rounded focus:outline-none focus:ring-2 ${inputCls}`}
            />
          </div>

          {/* Gender */}
          <div>
            <label className={`block ${textLabel} font-semibold mb-1`}>
              Gender
            </label>
            <Select
              name="gender"
              value={genderOptions.find(option => option.value === form.gender) || null}
              onChange={handleSelectChange}
              options={genderOptions}
              placeholder="Select Gender"
              className="react-select-container"
              classNamePrefix="react-select"
              styles={selectStyles}
              isClearable
            />
          </div>

          {/* DOB */}
          <div>
            <label className={`block ${textLabel} font-semibold mb-1`}>
              Date of Birth {form.dateOfBirth && (
                <span className={`text-sm ${textMuted} font-normal`}>
                  (Age: {calculateAge(form.dateOfBirth)} years)
                </span>
              )}
            </label>
            <DatePicker
              selected={form.dateOfBirth}
              onChange={(date) => handleDateChange(date, 'dateOfBirth')}
              dateFormat="dd/MM/yyyy"
              placeholderText="Select Date of Birth"
              maxDate={getMinEmployeeDate()}
              minDate={getMaxEmployeeDate()}
              showYearDropdown
              showMonthDropdown
              dropdownMode="select"
              yearDropdownItemNumber={50}
              scrollableYearDropdown
              className={`w-full p-2 border rounded focus:outline-none focus:ring-2 ${inputCls}`}
              wrapperClassName="w-full"
              autoComplete="bday"
              isClearable
              todayButton="Today"
            />
            {form.dateOfBirth && calculateAge(form.dateOfBirth) < 18 && (
              <p className="text-red-500 text-sm mt-1">
                Employee must be at least 18 years old
              </p>
            )}
          </div>

          {/* DOJ */}
          <div>
            <label className={`block ${textLabel} font-semibold mb-1`}>
              Date of Joining
              {form.dateOfJoining && form.dateOfBirth && (
                <span className={`text-sm ${textMuted} font-normal`}>
                  (Age at joining: {calculateAge(form.dateOfBirth, form.dateOfJoining)} years)
                </span>
              )}
            </label>
            <DatePicker
              selected={form.dateOfJoining}
              onChange={(date) => handleDateChange(date, 'dateOfJoining')}
              dateFormat="dd/MM/yyyy"
              placeholderText="Select Date of Joining"
              maxDate={new Date()}
              minDate={form.dateOfBirth || getMaxEmployeeDate()}
              showYearDropdown
              showMonthDropdown
              dropdownMode="select"
              yearDropdownItemNumber={20}
              scrollableYearDropdown
              className={`w-full p-2 border rounded focus:outline-none focus:ring-2 ${inputCls}`}
              wrapperClassName="w-full"
              isClearable
              todayButton="Today"
            />
            {form.dateOfJoining && form.dateOfBirth && form.dateOfJoining < form.dateOfBirth && (
              <p className="text-red-500 text-sm mt-1">
                Joining date cannot be before birth date
              </p>
            )}
          </div>

          {/* Aadhar */}
          <div>
            <label className={`block ${textLabel} font-semibold mb-1`}>
              Aadhar Number
            </label>
            <input
              type="text"
              name="aadharNo"
              value={form.aadharNo}
              onChange={handleInputChange}
              placeholder="Aadhar Number"
              className={`w-full p-2 border rounded focus:outline-none focus:ring-2 ${inputCls}`}
            />
          </div>

          {/* PAN */}
          <div>
            <label className={`block ${textLabel} font-semibold mb-1`}>
              PAN Number
            </label>
            <input
              type="text"
              name="panNo"
              value={form.panNo}
              onChange={handleInputChange}
              placeholder="PAN Number"
              className={`w-full p-2 border rounded focus:outline-none focus:ring-2 ${inputCls}`}
            />
          </div>
        </div>

        {/* Address & Family Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Postal Address */}
          <div>
            <label className={`block ${textLabel} font-semibold mb-1`}>
              Postal Address
            </label>
            <textarea
              name="postalAddress"
              value={form.postalAddress}
              onChange={handleInputChange}
              placeholder="Postal Address"
              rows={3}
              className={`w-full p-2 border rounded focus:outline-none focus:ring-2 ${inputCls}`}
            />
          </div>

          {/* Father, Mother, Spouse */}
          <div className="grid grid-cols-1 gap-4">
            <div>
              <label className={`block ${textLabel} font-semibold mb-1`}>
                Father's Name
              </label>
              <input
                type="text"
                name="fatherName"
                value={form.fatherName}
                onChange={handleInputChange}
                placeholder="Father's Name"
                className={`w-full p-2 border rounded focus:outline-none focus:ring-2 ${inputCls}`}
              />
            </div>
            <div>
              <label className={`block ${textLabel} font-semibold mb-1`}>
                Mother's Name
              </label>
              <input
                type="text"
                name="motherName"
                value={form.motherName}
                onChange={handleInputChange}
                placeholder="Mother's Name"
                className={`w-full p-2 border rounded focus:outline-none focus:ring-2 ${inputCls}`}
              />
            </div>
            <div>
              <label className={`block ${textLabel} font-semibold mb-1`}>
                Spouse Name
              </label>
              <input
                type="text"
                name="spouseName"
                value={form.spouseName}
                onChange={handleInputChange}
                placeholder="Spouse Name"
                className={`w-full p-2 border rounded focus:outline-none focus:ring-2 ${inputCls}`}
              />
            </div>
          </div>
        </div>

        {/* Phone Numbers */}
        <div>
          <label className={`block ${textLabel} font-semibold mb-2`}>
            Phone Numbers
          </label>
          {form.phoneNumbers.map((phone, index) => (
            <div key={index} className="flex items-center mb-2 gap-2">
              <select
                value={phone.countryCode || '+91'}
                onChange={(e) => handleCountryCodeChange(index, e.target.value)}
                className={`w-24 p-2 border rounded focus:outline-none focus:ring-2 text-sm ${inputCls}`}
              >
                <option value="+91">+91 IN</option>
                <option value="+1">+1 US</option>
                <option value="+44">+44 UK</option>
                <option value="+971">+971 UAE</option>
                <option value="+966">+966 SA</option>
                <option value="+65">+65 SG</option>
                <option value="+61">+61 AU</option>
                <option value="+880">+880 BD</option>
                <option value="+977">+977 NP</option>
                <option value="+94">+94 LK</option>
                <option value="+86">+86 CN</option>
                <option value="+81">+81 JP</option>
                <option value="+49">+49 DE</option>
                <option value="+33">+33 FR</option>
                <option value="+39">+39 IT</option>
              </select>
              <input
                type="text"
                value={phone.number}
                onChange={(e) => handlePhoneNumberChange(index, e.target.value)}
                placeholder="Phone Number"
                className={`flex-1 p-2 border rounded focus:outline-none focus:ring-2 ${inputCls}`}
              />
              <button
                type="button"
                onClick={() => removePhoneNumber(index)}
                className="bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600"
              >
                Remove
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={addPhoneNumber}
            className="mt-2 bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
          >
            Add Phone Number
          </button>
        </div>

        {/* Children Info */}
        <div>
          <label className={`block ${textLabel} font-semibold mb-2`}>
            Children
          </label>
          {form.children.map((child, index) => (
            <div key={index} className="flex flex-wrap items-center mb-2 gap-2">
              <input
                type="text"
                value={child.name}
                onChange={(e) => handleChildChange(index, 'name', e.target.value)}
                placeholder="Child Name"
                className={`flex-1 min-w-[150px] p-2 border rounded focus:outline-none focus:ring-2 ${inputCls}`}
              />
              <div className="flex flex-col">
                <DatePicker
                  selected={child.dateOfBirth}
                  onChange={(date) => handleChildDateChange(index, date)}
                  dateFormat="dd/MM/yyyy"
                  placeholderText="Child DOB"
                  maxDate={new Date()}
                  showYearDropdown
                  showMonthDropdown
                  dropdownMode="select"
                  yearDropdownItemNumber={30}
                  scrollableYearDropdown
                  className={`p-2 border rounded focus:outline-none focus:ring-2 ${inputCls}`}
                  wrapperClassName="min-w-[140px]"
                  isClearable
                />
                {child.dateOfBirth && (
                  <span className={`text-xs ${textMuted} mt-1`}>
                    Age: {calculateAge(child.dateOfBirth)} years
                  </span>
                )}
              </div>
              <Select
                name={`child-gender-${index}`}
                value={genderOptions.find(option => option.value === child.gender) || null}
                onChange={(selectedOption) => handleChildSelectChange(index, selectedOption)}
                options={genderOptions}
                placeholder="Gender"
                className="react-select-container w-32"
                classNamePrefix="react-select"
                styles={selectStyles}
                isClearable
              />
              <button
                type="button"
                onClick={() => removeChild(index)}
                className="bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600"
              >
                Remove
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={addChild}
            className="mt-2 bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
          >
            Add Child
          </button>
        </div>

        {/* Bank / Other Info Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <label className={`block ${textLabel} font-semibold mb-1`}>
              Account Name
            </label>
            <input
              type="text"
              name="accountName"
              value={form.accountName}
              onChange={handleInputChange}
              placeholder="Account Name"
              className={`w-full p-2 border rounded focus:outline-none focus:ring-2 ${inputCls}`}
            />
          </div>
          <div>
            <label className={`block ${textLabel} font-semibold mb-1`}>
              Account Number
            </label>
            <input
              type="text"
              name="accountNo"
              value={form.accountNo}
              onChange={handleInputChange}
              placeholder="Account Number"
              className={`w-full p-2 border rounded focus:outline-none focus:ring-2 ${inputCls}`}
            />
          </div>
          <div>
            <label className={`block ${textLabel} font-semibold mb-1`}>
              Bank Name
            </label>
            <input
              type="text"
              name="bankName"
              value={form.bankName}
              onChange={handleInputChange}
              placeholder="Bank Name"
              className={`w-full p-2 border rounded focus:outline-none focus:ring-2 ${inputCls}`}
            />
          </div>
          <div>
            <label className={`block ${textLabel} font-semibold mb-1`}>
              IFSC Code
            </label>
            <input
              type="text"
              name="ifsc"
              value={form.ifsc}
              onChange={handleInputChange}
              placeholder="IFSC Code"
              className={`w-full p-2 border rounded focus:outline-none focus:ring-2 ${inputCls}`}
            />
          </div>
          <div>
            <label className={`block ${textLabel} font-semibold mb-1`}>
              PF Number
            </label>
            <input
              type="text"
              name="pfNo"
              value={form.pfNo}
              onChange={handleInputChange}
              placeholder="PF Number"
              className={`w-full p-2 border rounded focus:outline-none focus:ring-2 ${inputCls}`}
            />
          </div>
          <div>
            <label className={`block ${textLabel} font-semibold mb-1`}>
              ESIC Number
            </label>
            <input
              type="text"
              name="esicNo"
              value={form.esicNo}
              onChange={handleInputChange}
              placeholder="ESIC Number"
              className={`w-full p-2 border rounded focus:outline-none focus:ring-2 ${inputCls}`}
            />
          </div>
          <div>
            <label className={`block font-medium ${textLabel}`}>Role*</label>
            <Select
              name="role"
              value={roles.find(role => role._id === form.role) ? { value: form.role, label: roles.find(role => role._id === form.role)?.postName } : null}
              onChange={handleSelectChange}
              options={roles.map(role => ({ value: role._id, label: role.postName }))}
              placeholder="Select Role"
              className="react-select-container mt-1"
              classNamePrefix="react-select"
              styles={selectStyles}
              required
              isClearable
            />
          </div>
          <div>
            <label className={`block ${textLabel} font-semibold mb-1`}>
              Password
            </label>
            <input
              type="password"
              name="password"
              value={form.password}
              onChange={handleInputChange}
              placeholder="Password"
              className={`w-full p-2 border rounded focus:outline-none focus:ring-2 ${inputCls}`}
            />
          </div>          {/* Attendance Type */}
          <div>
            <label className={`block ${textLabel} font-semibold mb-1`}>
              Attendance Type
            </label>
            <Select
              name="attendanceType"
              value={attendanceTypeOptions.find(option => option.value === form.attendanceType) || null}
              onChange={handleSelectChange}
              options={attendanceTypeOptions}
              placeholder="Select Attendance Type"
              className="react-select-container"
              classNamePrefix="react-select"
              styles={selectStyles}
              isClearable
            />
          </div>

          {/* Is Salesman */}
          <div>
            <label className={`block ${textLabel} font-semibold mb-1`}>
              Is Salesperson
            </label>
            <div className="mt-2">
              <label className="inline-flex items-center">
                <input
                  type="checkbox"
                  name="isSalesman"
                  checked={form.isSalesperson}
                  onChange={(e) => setForm(prev => ({ ...prev, isSalesman: e.target.checked }))}
                  className="form-checkbox h-5 w-5 text-blue-600"
                />
                <span className={`ml-2 ${textLabel}`}>Yes, this employee is a salesperson</span>
              </label>
            </div>
          </div>

          {/* Is TT */}
          <div>
            <label className={`block ${textLabel} font-semibold mb-1`}>
              TT Assignment Access
            </label>
            <div className="mt-2">
              <label className="inline-flex items-center">
                <input
                  type="checkbox"
                  name="isTT"
                  checked={form.isTT}
                  onChange={(e) => setForm(prev => ({ ...prev, isTT: e.target.checked }))}
                  className="form-checkbox h-5 w-5 text-green-600"
                />
                <span className={`ml-2 ${textLabel}`}>Can assign TT values when reference is present</span>
              </label>
            </div>
          </div>
        </div>

        {/* Draft Settings Section */}
        <div className={`${darkMode ? 'bg-gray-800 border-gray-600' : 'bg-blue-50 border-blue-200'} p-4 rounded-lg border`}>
          <h3 className={`text-lg font-semibold ${darkMode ? 'text-blue-300' : 'text-blue-800'} mb-3`}>Draft Settings</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Draft Enabled */}
            <div>
              <label className={`block ${textLabel} font-semibold mb-1`}>
                Enable Draft Feature
              </label>
              <div className="mt-2">
                <label className="inline-flex items-center">
                  <input
                    type="checkbox"
                    name="draftEnabled"
                    checked={form.draftSettings?.draftEnabled ?? true}
                    onChange={(e) => setForm(prev => ({ 
                      ...prev, 
                      draftSettings: {
                        ...prev.draftSettings,
                        draftEnabled: e.target.checked
                      }
                    }))}
                    className="form-checkbox h-5 w-5 text-blue-600"
                  />
                  <span className={`ml-2 ${textLabel}`}>Allow this employee to save drafts</span>
                </label>
              </div>
            </div>

            {/* Draft Duration */}
            <div>
              <label className={`block ${textLabel} font-semibold mb-1 ${!(form.draftSettings?.draftEnabled ?? true) ? 'opacity-50' : ''}`}>
                Draft Retention Duration
              </label>
              <Select
                name="draftDuration"
                value={draftDurationOptions.find(option => option.value === (form.draftSettings?.draftDuration || '1week')) || draftDurationOptions[2]}
                onChange={(selectedOption) => setForm(prev => ({ 
                  ...prev, 
                  draftSettings: {
                    ...prev.draftSettings,
                    draftDuration: selectedOption?.value || '1week'
                  }
                }))}
                options={draftDurationOptions}
                placeholder="Select Duration"
                className="react-select-container"
                classNamePrefix="react-select"
                styles={selectStyles}
                isDisabled={!(form.draftSettings?.draftEnabled ?? true)}
              />
              <small className={`${textMuted} mt-1 block ${!(form.draftSettings?.draftEnabled ?? true) ? 'opacity-50' : ''}`}>
                How long drafts should be kept before automatic deletion
              </small>
            </div>
          </div>
        </div>

        {/* Image + Email + Location */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Image Upload */}
          <div>
            <label className={`block ${textLabel} font-semibold mb-1`}>
              Employee Image
            </label>
            <div className={`cursor-pointer flex items-center border-2 border-dashed ${darkMode ? 'border-blue-400 hover:bg-gray-700' : 'border-blue-500 hover:bg-blue-50'} rounded p-2`}>
              <input
                accept="image/*"
                type="file"
                name="image"
                onChange={handleImageChange}
                className="hidden"
                id="imageUpload"
              />
              <span
                className="text-blue-500 font-medium"
                onClick={() => document.getElementById('imageUpload').click()}
              >
                Upload Image
              </span>
            </div>
            {imagePreview && (
              <div className="mt-2">
                <img
                  src={imagePreview}
                  alt="Employee"
                  className="w-16 h-16 object-cover rounded"
                />
              </div>
            )}
          </div>

          {/* Email */}
          <div>
            <label className={`block ${textLabel} font-semibold mb-1`}>
              Email ID
            </label>
            <input
              type="email"
              name="email"
              value={form.email}
              onChange={handleInputChange}
              placeholder="Email ID"
              className={`w-full p-2 border rounded focus:outline-none focus:ring-2 ${inputCls}`}
            />
          </div>

          {/* Location */}
          <div>
            <label className={`block ${textLabel} font-semibold mb-1`}>
              Assigned Location
            </label>
            <Select
              name="assignedLocation"
              value={locations.find(loc => loc._id === form.assignedLocation) ? { value: form.assignedLocation, label: locations.find(loc => loc._id === form.assignedLocation)?.name } : null}
              onChange={handleSelectChange}
              options={locations.map(loc => ({ value: loc._id, label: loc.name }))}
              placeholder="Select Location"
              className="react-select-container"
              classNamePrefix="react-select"
              styles={selectStyles}
              isClearable
            />
          </div>
        </div>

        {/* Submit / Cancel Buttons */}
        <div className="flex justify-end items-center space-x-2">
          <button
            type="submit"
            className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-400"
          >
            {isEditing ? 'Update Employee' : 'Add Employee'}
          </button>
          {isEditing && (
            <button
              type="button"
              onClick={resetForm} 
           className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-400"
            >
              Cancel
            </button>
          )}
        </div>
      </form>

      {/* EMPLOYEES LIST SECTION */}
      <h2 className="text-xl md:text-2xl font-bold mt-12 mb-4">Employees</h2>

      {/* Filter & Page-Size Control */}
      <div className="flex flex-wrap items-center gap-2 mb-6">
        <input
          type="text"
          placeholder="Search employees..."
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className={`flex-grow p-2 border rounded focus:outline-none focus:ring-2 ${inputCls}`}
        />
        <Select
          name="limit"
          value={limitOptions.find(option => option.value === limit) || limitOptions[0]}
          onChange={handleLimitChange}
          options={limitOptions}
          className="react-select-container w-28"
          classNamePrefix="react-select"
          styles={selectStyles}
          isSearchable={false}
        />
        <button
          onClick={handleExportExcel}
          className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-400 flex items-center gap-2"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          Export Excel
        </button>
      </div>

      {/* Desktop Table */}
      <div className="hidden md:block overflow-x-auto">
        <table className={`min-w-full border-collapse divide-y ${darkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
          <thead className={headerBg}>
            <tr>
              <th className={`px-4 py-3 text-left text-xs font-medium ${textMuted} uppercase`}>ID</th>
              <th className={`px-4 py-3 text-left text-xs font-medium ${textMuted} uppercase`}>Name</th>
              <th className={`px-4 py-3 text-left text-xs font-medium ${textMuted} uppercase`}>Email</th>
              <th className={`px-4 py-3 text-left text-xs font-medium ${textMuted} uppercase`}>Attendance</th>
              <th className={`px-4 py-3 text-left text-xs font-medium ${textMuted} uppercase`}>Actions</th>
            </tr>
          </thead>
          <tbody className={`divide-y ${darkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
            {(filteredEmployees.length === 0 && page !== 0) ? setPage(page - 1) : null}
            {filteredEmployees.length === 0 ? (
              <tr>
                <td colSpan="5" className={`px-4 py-3 text-sm ${textMuted}`}>
                  No employees found.
                </td>
              </tr>
            ) : (
              filteredEmployees.map((employee) => (
                <tr key={employee._id} className={hoverRow}>
                  <td className={`px-4 py-3 text-sm ${textMuted} whitespace-nowrap`}>
                    {employee.id}
                  </td>
                  <td className={`px-4 py-3 text-sm font-medium ${text} whitespace-nowrap`}>
                    {employee.name}
                  </td>
                  <td className={`px-4 py-3 text-sm ${textMuted} whitespace-nowrap`}>
                    {employee.email}
                  </td>
                  <td className={`px-4 py-3 text-sm ${textMuted} whitespace-nowrap`}>
                    {employee.attendanceType}
                  </td>
                  <td className="px-4 py-3 text-sm text-right whitespace-nowrap">
                    <button
                      onClick={() => startEditing(employee)}
                      className="text-indigo-600 hover:text-indigo-900 mr-3"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleViewDetails(employee)}
                      className="text-blue-600 hover:text-blue-900 mr-3"
                    >
                      View Details
                    </button>
                    <button
                      onClick={() => toggleActive(employee)}
                      className={`mr-3 ${employee.isActive ? "text-red-600 hover:text-red-900" : "text-green-600 hover:text-green-900"}`}
                    >
                      {employee.isActive ? "Deactivate" : "Activate"}
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile Cards */}
      <div className="block md:hidden space-y-4">
        {filteredEmployees.map((employee) => (
          <div key={employee._id} className={`${cardBg} rounded-lg p-4 shadow flex flex-col`}>
            <div className="flex justify-between items-center mb-2">
              <div>
                <p className={`text-sm ${textMuted}`}>ID: {employee.id}</p>
                <p className={`text-lg font-semibold ${text}`}>{employee.name}</p>
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => startEditing(employee)}
                  className="text-sm bg-indigo-600 text-white px-2 py-1 rounded hover:bg-indigo-700"
                >
                  Edit
                </button>
                <button
                  onClick={() => handleViewDetails(employee)}
                  className="text-sm text-blue-600 hover:text-blue-900"
                >
                  View Details
                </button>
              </div>
            </div>
            <div className={`${textMuted} mb-2`}>
              <p>Email: {employee.email}</p>
              <p>Attendance: {employee.attendanceType}</p>
            </div>
            <div className="text-right">
              <button
                onClick={() => toggleActive(employee)}
                className={`text-sm font-semibold ${employee.isActive ? "text-red-600 hover:text-red-900" : "text-green-600 hover:text-green-900"}`}
              >
                {employee.isActive ? "Deactivate" : "Activate"}
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Pagination Controls */}
      <div className="flex items-center justify-center md:justify-between mt-6">
        {/* Previous Button */}
        <button
          onClick={handlePreviousPage}
          disabled={currentPage === 1}
          className={`flex items-center px-4 py-2 rounded disabled:opacity-50 disabled:cursor-not-allowed ${darkMode ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
        >
          <ChevronLeftIcon className="h-5 w-5 mr-1" />
          Previous
        </button>

        {/* Page Info */}
        <span className="mx-4">
          Page {currentPage} of {Math.ceil(totalEmployees / limit) || 1}
        </span>

        {/* Next Button */}
        <button
          onClick={handleNextPage}
          disabled={currentPage >= Math.ceil(totalEmployees / limit)}
          className={`flex items-center px-4 py-2 rounded disabled:opacity-50 disabled:cursor-not-allowed ${darkMode ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
        >
          Next
          <ChevronRightIcon className="h-5 w-5 ml-1" />
        </button>
      </div>

      {/* Details Modal */}
      {showDetailsModal && <DetailsModal />}

      {/* Custom styles for react-select */}
      <style jsx>{`
        .react-select-container .react-select__control {
          border: 1px solid #d1d5db;
          border-radius: 0.375rem;
          min-height: 42px;
          box-shadow: none;
        }
        
        .react-select-container .react-select__control:hover {
          border-color: #d1d5db;
        }
        
        .react-select-container .react-select__control--is-focused {
          border-color: #3b82f6;
          box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.5);
        }
        
        .react-select-container .react-select__value-container {
          padding: 2px 8px;
        }
        
        .react-select-container .react-select__input-container {
          margin: 0;
          padding: 0;
        }
        
        .react-select-container .react-select__placeholder {
          color: #9ca3af;
        }
        
        .react-select-container .react-select__single-value {
          color: #374151;
        }
        
        .react-select-container .react-select__indicator-separator {
          background-color: #d1d5db;
        }
        
        .react-select-container .react-select__dropdown-indicator {
          color: #6b7280;
        }
        
        .react-select-container .react-select__menu {
          border: 1px solid #d1d5db;
          border-radius: 0.375rem;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
        }
        
        .react-select-container .react-select__option {
          padding: 8px 12px;
        }
        
        .react-select-container .react-select__option--is-selected {
          background-color: #3b82f6;
        }
        
        .react-select-container .react-select__option--is-focused {
          background-color: #dbeafe;
          color: #1f2937;
        }

        /* Custom styles for react-datepicker */
        .react-datepicker-wrapper {
          width: 100%;
        }
        
        .react-datepicker__input-container input {
          width: 100%;
        }
        
        .react-datepicker {
          font-family: inherit;
          border: 1px solid #d1d5db;
          border-radius: 0.5rem;
          box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
        }
        
        .react-datepicker__header {
          background-color: #f3f4f6;
          border-bottom: 1px solid #d1d5db;
          border-radius: 0.5rem 0.5rem 0 0;
        }
        
        .react-datepicker__current-month {
          font-weight: 600;
          color: #374151;
        }
        
        .react-datepicker__day-name {
          color: #6b7280;
          font-weight: 500;
        }
        
        .react-datepicker__day {
          border-radius: 0.375rem;
          transition: all 0.2s;
        }
        
        .react-datepicker__day:hover {
          background-color: #dbeafe;
        }
        
        .react-datepicker__day--selected {
          background-color: #3b82f6;
          color: white;
        }
        
        .react-datepicker__day--today {
          background-color: #fef3c7;
          color: #92400e;
        }
        
        .react-datepicker__navigation {
          border: none;
          border-radius: 0.375rem;
        }
        
        .react-datepicker__navigation:hover {
          background-color: #e5e7eb;
        }
        
        .react-datepicker__month-dropdown,
        .react-datepicker__year-dropdown {
          background-color: white;
          border: 1px solid #d1d5db;
          border-radius: 0.375rem;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
        }
        
        .react-datepicker__month-option,
        .react-datepicker__year-option {
          padding: 0.5rem;
          border-radius: 0.25rem;
          transition: background-color 0.2s;
        }
        
        .react-datepicker__month-option:hover,
        .react-datepicker__year-option:hover {
          background-color: #f3f4f6;
        }
        
        .react-datepicker__month-option--selected,
        .react-datepicker__year-option--selected {
          background-color: #3b82f6;
          color: white;
        }
      `}</style>
    </div>
  );
};

export default EmployeeManagement;